import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/orders/grid/table';
import {OrdersGridFormattedRow, OrdersGridRawRow, GridFieldConfig} from 'types';

interface Props {
    configFunc: (t: TFunction) => GridFieldConfig[];
    dataMapper: (data: OrdersGridRawRow[] | undefined, t: TFunction) => OrdersGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const OrdersGridTable = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const {data, totalCount, pageNumber, pageSize, filters, sortings, dataLoading} = useStore(
        tableState$,
    );
    useGate(Gate);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const mappedData = React.useMemo(() => dataMapper(data, t), [dataMapper, data, t]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={totalCount}
            pageCount={Math.ceil(totalCount / pageSize)}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={dataLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'ordersGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
